/**
 * Store Links Utilities
 * Utilities for App Store and Play Store navigation
 */

import { openInAppBrowser } from './browser.utils';
import { isNative } from './platform.utils';

/**
 * Get store link for current platform
 * @param {string} platform - Platform ('android', 'ios', 'web')
 * @returns {string} Store URL
 */
export const getStoreLink = (platform = 'web') => {
  const storeUrls = {
    android: ' https://play.google.com/store/apps/details?id=com.oceanfinvest.inxits',
    ios: 'https://apps.apple.com/app/com.oceanfinvest.inxits',
    web: '/'
  };
  
  return storeUrls[platform] || storeUrls.web;
};

/**
 * Open appropriate store for current platform
 * @param {string} platform - Platform ('android', 'ios', 'web')
 * @param {boolean} useInAppBrowser - Use in-app browser (default: true)
 * @returns {Promise<void>}
 */
export const openStore = async (platform = 'web', useInAppBrowser = true) => {
  try {
    const storeUrl = getStoreLink(platform);
    
    // Use in-app browser for native platforms if enabled
    if (useInAppBrowser && isNative()) {
      await openInAppBrowser(storeUrl, {
        mode: 'system',
        presentationStyle: 'fullscreen'
      });
      return;
    }
    
    // Fallback to external browser
    if (platform === 'android') {
      // Try Play Store app, fallback to web
      // window.location.href = `market://details?id=com.inxits.customer`;
      
      // Fallback to web Play Store after timeout
      setTimeout(() => {
        window.open(storeUrl, '_blank');
      }, 1000);
    } else if (platform === 'ios') {
      // Try App Store app, fallback to web
      window.location.href = storeUrl;
    } else {
      // Web platform - just open the link
      window.open(storeUrl, '_blank');
    }
  } catch (error) {
    console.error('Failed to open store:', error);
    // Fallback to web
    window.open(getStoreLink(platform), '_blank');
  }
};

/**
 * Open App Store (iOS)
 * @returns {Promise<void>}
 */
export const openAppStore = () => {
  return openStore('ios');
};

/**
 * Open Play Store (Android)
 * @returns {Promise<void>}
 */
export const openPlayStore = () => {
  return openStore('android');
};

/**
 * Get store name for platform
 * @param {string} platform - Platform
 * @returns {string} Store name
 */
export const getStoreName = (platform) => {
  const storeNames = {
    android: 'Google Play Store',
    ios: 'App Store',
    web: 'Web App'
  };
  
  return storeNames[platform] || 'Store';
};

/**
 * Get update button text based on platform
 * @param {string} platform - Platform
 * @param {boolean} isForceUpdate - Is this a force update
 * @returns {string} Button text
 */
export const getUpdateButtonText = (platform, isForceUpdate = false) => {
  if (isForceUpdate) {
    return 'Update Now';
  }
  
  const storeName = getStoreName(platform);
  
  if (platform === 'android') {
    return `Update on ${storeName}`;
  } else if (platform === 'ios') {
    return `Update on ${storeName}`;
  }
  
  return 'Update';
};

/**
 * Get store URL from the API response.
 * Supports:
 *  - Plain string  → returned directly from `store_link` in the API response
 *  - Legacy object → { android: '...', ios: '...', web: '...' }
 *
 * @param {string|Object|null} updateUrl - store_link value from API
 * @param {string} platform - Platform ('android', 'ios', 'web')
 * @returns {string|null} Resolved store URL
 */
export const getStoreUrl = (updateUrl, platform) => {
  if (!updateUrl) return null;

  // Plain string URL returned directly from the API (e.g. store_link field)
  if (typeof updateUrl === 'string') {
    return updateUrl;
  }

  // Legacy object format: { android, ios, web }
  if (updateUrl[platform]) {
    return updateUrl[platform];
  }

  if (updateUrl.web) {
    return updateUrl.web;
  }

  // Final fallback: hardcoded store link for the platform
  return getStoreLink(platform);
};
